<script setup lang="ts">
  import { computed } from 'vue'

  import CBtn from '@shared/components/ui/c-btn.vue'
  import SvgIcon from '@shared/components/svg-icon.vue'

  const props = withDefaults(
    defineProps<{
      isShow: boolean
      title: string
      text?: string
      hasSubtitle?: boolean
      subtitle?: string
      cancelBtnText?: string
      submitBtnText?: string
      isCloseCrossBtn?: boolean
      isBottomControls?: boolean
      isHideCancelButton?: boolean
      closeOnClickModal?: boolean
      closeOnPressEscape?: boolean
      isDangerSubmitBtn?: boolean
      isFullscreen?: boolean
    }>(),
    {
      hasSubtitle: false,
      subtitle: '',
      cancelBtnText: 'Cancel',
      submitBtnText: 'Continue',
      isCloseCrossBtn: false,
      isBottomControls: true,
      text: '',
      isHideCancelButton: false,
      closeOnClickModal: true,
      closeOnPressEscape: true,
    }
  )

  const isShowPopup = computed({
    get() {
      return props.isShow
    },
    set() {
      emit('close')
    },
  })

  const emit = defineEmits(['close', 'submit'])
</script>

<template>
  <el-dialog
    v-model="isShowPopup"
    align-center
    width="485px"
    :fullscreen="isFullscreen"
    :show-close="false"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
  >
    <template v-if="$slots['header']" #header>
      <slot name="header" />
    </template>
    <template v-else #header>
      <template v-if="isCloseCrossBtn">
        <div class="tw-flex tw-justify-between">
          <div v-if="hasSubtitle">
            <span>{{ subtitle }}</span>
            <h2 class="tw-text-headline-2">{{ title }}</h2>
          </div>
          <span v-else class="tw-text-headline-4">
            {{ title }}
          </span>
          <button @click="emit('close')">
            <svg-icon class="tw-fill-primary-100" name="remove" size="16" />
          </button>
        </div>
        <el-divider class="tw-border-grey-400" />
      </template>
      <div v-else class="tw-text-center">
        <span class="tw-text-headline-2 tw-text-primary-300 tw-break-normal">
          {{ title }}
        </span>
      </div>
    </template>

    <template v-if="$slots['body']">
      <slot name="body" />
    </template>
    <div v-else class="tw-flex tw-items-center tw-flex-col" :class="{ 'tw-mb-24': isCloseCrossBtn }">
      <pre class="tw-text-body-1 tw-whitespace-pre-line tw-text-center tw-break-normal tw-font-sans">
        {{ text }}
      </pre>
      <el-divider v-if="!isCloseCrossBtn" class="tw-border-grey-400" />
    </div>
    <template v-if="isBottomControls" #footer>
      <slot v-if="$slots['footer']" name="footer" />
      <div v-else class="tw-flex tw-items-center tw-justify-center">
        <c-btn v-if="!isHideCancelButton" outlined class="tw-mr-16" @click="emit('close')">{{ cancelBtnText }}</c-btn>
        <c-btn :accent="isDangerSubmitBtn" @click="emit('submit')">{{ submitBtnText }}</c-btn>
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss">
  .el-dialog {
    @apply tw-rounded-[12px] tw-p-32;
    &__header {
      @apply tw-mr-0 tw-p-0 tw-mb-16;
    }
    &__body {
      @apply tw-p-0;
    }
    &__footer {
      @apply tw-p-0;
    }
  }
</style>
